import React from 'react';
import {
  css,
  ThemeProvider as EmotionThemeProvider,
  Global,
} from '@emotion/react';
import _, { chain } from 'lodash';
import { type FunnelLocalState } from '@bbk/retention-sdk/storage';
import PricingTableHelper from '@bbkAdminComponents/experiences/pricing-pages-editors/helper/pricing-table-helper';
import {
  type ExperienceCardTemplate,
  type ExperienceFullWidthHeaderCard,
  type ExperienceMultiRowSurveyCard,
  type ExperienceSimpleHeaderCard,
  type ExperienceSimplePleaCard,
  type ExperienceSimplePleaWithReturnCard,
  type PricingPageHeaderCard,
} from '@bbkAdminRedux/rtkq/experience.slice';
import { type RestPricingPageLayout } from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import { type RestModalCard } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';
import { type Maybe } from '@bbkAdminUtils/utility-types';
import { Loss } from '@bbkFunnel/components/loss/loss-aversion-cards';
import { type NextStepHandler } from '@bbkFunnel/events';
import {
  type BackwardsObject,
  type ExecuteEventsArg,
  type FunnelProps,
} from '@bbkFunnel/pages/Funnel';
import {
  type FunnelDerived,
  type FunnelSurvey,
} from '@bbkFunnel/pages/funnel-state';
import { ThemeKeys } from '@bbkFunnel/pages/theme';
import Container from '@bbkFunnelComponents/container';
import {
  FunnelContext,
  isAdminFunnelSurface,
} from '@bbkFunnelComponents/FunnelContext';
import { FullWidthHeader } from '@bbkFunnelComponents/header/header-full-width';
import { SimpleHeader } from '@bbkFunnelComponents/header/header-simple';
import {
  Modal,
  type ModalHandleAnyInfo,
  type ModalOnClose,
} from '@bbkFunnelComponents/modal';
import { type CompetitorOtherTextHandler } from '@bbkFunnelComponents/modals/sections/competition-other-text';
import { LoadingOverlay } from '@bbkFunnelComponents/overlay';
import { Plea } from '@bbkFunnelComponents/plea/plea';
import { RenderErrorBoundary } from '@bbkFunnelComponents/render-error-boundary';
import { type CompetitorOtherTextClearHandler } from '@bbkFunnelComponents/survey/competition/competition-content';
import { type ReasonOtherTextClearHandler } from '@bbkFunnelComponents/survey/reasons/reasons-content';
import { type ReasonOtherHandler } from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import {
  SurveyMultiRow,
  type DirectFromSurveyCancelHandler,
  type SurveyMultiRowResponseHandler,
} from '@bbkFunnelComponents/survey-multi-row';
import { Warn } from '@bbkFunnelComponents/warn/warn';
import PricingPageHeader from '@bbkPricingPage/pricing-page-header';
import PricingTable from '@bbkPricingPage/pricing-table';
import { type PricingTable as PricingTableType } from '@bbkPricingPage/types/pricing-table-types';

/**
 * `Funnel` owns session state, the derived model and every handler; this owns
 * the markup. The split exists so the orchestration can be converted to hooks
 * without the presentation in the same diff.
 *
 * `fullInfo` is `Funnel`'s own props object rather than a rebuilt subset,
 * because four children take it wholesale and `Modal` reaches past its declared
 * `Pick<...>` for `user` through `_.get`. Spreading it here would drop fields
 * that no type would miss.
 */
export type FunnelViewProps = {
  fullInfo: FunnelProps;
  derived: FunnelDerived;
  survey: FunnelSurvey;

  showModal: boolean;
  showLoader: boolean;
  showOpaqueLoader: boolean;
  modal: RestModalCard | undefined;
  localState?: FunnelLocalState;

  executeEvents: (eventsAndSyncs: ExecuteEventsArg) => void;
  nextStep: NextStepHandler;
  nevermind: ModalOnClose;
  continueFn: () => void;
  handleResponseMulti: SurveyMultiRowResponseHandler;
  handleReasonOtherText: ReasonOtherHandler;
  clearReasonOtherText: ReasonOtherTextClearHandler;
  updateCompetitionOtherText: CompetitorOtherTextHandler;
  clearCompetitionOtherText: CompetitorOtherTextClearHandler;
  handleBackwards: (backObj: BackwardsObject) => void;
  directFromSurveyCancel: DirectFromSurveyCancelHandler;
  showLoaderOverlay: () => void;
  handleAnyInformation: ModalHandleAnyInfo;
};

export const FunnelView = (props: FunnelViewProps) => {
  const {
    fullInfo,
    derived,
    survey,
    showModal,
    showLoader,
    showOpaqueLoader,
    modal,
    localState,
    executeEvents,
    nextStep,
    nevermind,
    continueFn,
    handleResponseMulti,
    handleReasonOtherText,
    clearReasonOtherText,
    updateCompetitionOtherText,
    clearCompetitionOtherText,
    handleBackwards,
    directFromSurveyCancel,
    showLoaderOverlay,
    handleAnyInformation,
  } = props;

  const funnelContext = React.useContext(FunnelContext);
  const { isPreview, isHostedPage, isPricingPage, pricingPageProps } =
    funnelContext;

  const {
    session,
    version,
    user,
    layout,
    country,
    isLifecycle,
    emotionTheme,
    disableThemeProvider,
    lossAversionLayout,
    isLossAversionLoading,
    lossAversionErrors,
    sendLCOEventsToParent,
  } = fullInfo;

  const {
    cardsByTemplate,
    content,
    surveySection,
    questions,
    surveyType,
    presentation,
  } = derived;

  const getCardByTemplate = <T,>(
    template: keyof typeof ExperienceCardTemplate
  ) => cardsByTemplate.get(template) as Maybe<T>;

  /**
   * Rebuilt per child rather than hoisted, matching the class it came from:
   * these objects are handed to separate subtrees and sharing one instance
   * would couple them.
   */
  const syncData = () => ({
    session,
    version,
    app_id: (user && user.app_id) || null,
    survey_state: survey,
  });

  const renderFullWidthHeader = () => {
    const fullWidthHeaderCard =
      getCardByTemplate<ExperienceFullWidthHeaderCard>('full_width_header');

    return (
      fullWidthHeaderCard && (
        <FullWidthHeader card={fullWidthHeaderCard} fullInfo={fullInfo} />
      )
    );
  };

  const renderSimpleHeader = () => {
    const simpleHeaderCard =
      getCardByTemplate<ExperienceSimpleHeaderCard>('simple_header');
    return (
      simpleHeaderCard && (
        <SimpleHeader card={simpleHeaderCard} fullInfo={fullInfo} />
      )
    );
  };

  const renderSimplePlea = () => {
    const simplePleaCard =
      getCardByTemplate<ExperienceSimplePleaCard>('simple_plea');
    return (
      simplePleaCard && (
        <Plea
          card={simplePleaCard}
          fullWidthHeader={cardsByTemplate.has('full_width_header')}
        />
      )
    );
  };

  const renderPricingTable = () => {
    const discounts = pricingPageProps?.discounts;
    const subscription = pricingPageProps?.subscription;
    const pricingTable = isPreview
      ? (_.get(layout, 'pricing_table') as unknown as PricingTableType)
      : PricingTableHelper.getPricingTableFromLayout(
          layout as unknown as RestPricingPageLayout
        );
    return (
      <PricingTable
        pricingTable={pricingTable}
        subscription={subscription}
        discounts={discounts}
        country={country}
        defaultCurrency={pricingPageProps?.defaultCurrency}
        executeEvents={executeEvents}
      />
    );
  };

  const renderPricingPageHeader = () => {
    // Preview reads live layout; Funnel card state can lag form edits.
    const headerCard = isPreview
      ? (layout.cards.find(
          (card) => card.template === 'simple_header'
        ) as Maybe<PricingPageHeaderCard>)
      : getCardByTemplate<PricingPageHeaderCard>('simple_header');
    return headerCard && <PricingPageHeader card={headerCard} />;
  };

  const renderLoss = () => (
    <Loss
      isLifecycle={fullInfo.isLifecycle}
      cards={content}
      confirmationCards={layout.cards}
      executeEvents={executeEvents}
      syncData={syncData()}
      nextStep={nextStep}
      continueAction={continueFn}
      lossAversionLayout={lossAversionLayout}
      isLossAversionLoading={isLossAversionLoading}
      lossAversionErrors={lossAversionErrors}
    />
  );

  const renderSimplePleaWithReturn = () => {
    const simplePleaWithReturnCard =
      getCardByTemplate<ExperienceSimplePleaWithReturnCard>(
        'simple_plea_with_return'
      );

    return (
      simplePleaWithReturnCard && (
        <Warn
          card={simplePleaWithReturnCard}
          executeEvents={executeEvents}
          syncData={syncData()}
        />
      )
    );
  };

  const renderMultiRowSurvey = () => {
    const multiRowSurvey =
      getCardByTemplate<ExperienceMultiRowSurveyCard>('multi_row_survey');
    return (
      multiRowSurvey && (
        <SurveyMultiRow
          card={multiRowSurvey}
          response={survey}
          handler={handleResponseMulti}
          handleReasonOtherText={handleReasonOtherText}
          clearReasonOtherText={clearReasonOtherText}
          handleCompOtherText={updateCompetitionOtherText}
          clearCompOtherText={clearCompetitionOtherText}
          nextStep={nextStep}
          continue={continueFn}
          handleBackwards={handleBackwards}
          LS={localState!}
          directFromSurveyCancel={directFromSurveyCancel}
          loading={showLoader}
          isPreview={isPreview}
        />
      )
    );
  };

  const renderModal = () => (
    <Modal
      isLifecycle={isLifecycle}
      open={showModal}
      modal={modal ?? ({} as RestModalCard)}
      nextStep={nextStep}
      nevermind={nevermind}
      fullInfo={fullInfo}
      response={survey}
      executeEvents={executeEvents}
      sendLCOEventsToParent={sendLCOEventsToParent}
      fullSurvey={surveySection}
      questions={questions}
      surveyType={surveyType}
      showLoaderOverlay={showLoaderOverlay}
      handleAnyInformation={handleAnyInformation}
      presentation={presentation}
    />
  );

  const orderRenderEls = () => {
    const pleaCard = getCardByTemplate<ExperienceSimplePleaCard>('simple_plea');
    const pleaWithReturnCard =
      getCardByTemplate<ExperienceSimplePleaWithReturnCard>(
        'simple_plea_with_return'
      );
    const surveyCard =
      getCardByTemplate<ExperienceMultiRowSurveyCard>('multi_row_survey');

    const header = isPricingPage ? null : (
      <React.Fragment>
        {renderFullWidthHeader()}
        {renderSimpleHeader()}
      </React.Fragment>
    );
    const plea = <div className="container">{renderSimplePlea()}</div>;
    const laCards = <div className="container">{renderLoss()}</div>;
    const pleaWithReturn = (
      <div className="container">{renderSimplePleaWithReturn()}</div>
    );
    const surveyEl = (
      <div className="container pb-6">{renderMultiRowSurvey()}</div>
    );

    const pricingTable = (
      <div className="pricing-page tw-w-full">{renderPricingTable()}</div>
    );

    const pricingPageHeader = (
      <div className="pricing-page tw-w-full">{renderPricingPageHeader()}</div>
    );

    const funnelEls = [
      { key: 'header', el: header, order: 100 },
      { key: 'plea', el: plea, order: pleaCard?.order ?? 200 },
      { key: 'la', el: laCards, order: 300 },
      {
        key: 'pleaWithReturn',
        el: pleaWithReturn,
        order: pleaWithReturnCard?.order ?? 400,
      },
      { key: 'survey', el: surveyEl, order: surveyCard?.order ?? 500 },
    ];

    const pricingPageEls = [
      { key: 'header', el: pricingPageHeader, order: 100 },
      { key: 'pricingTable', el: pricingTable, order: 200 },
    ];

    return chain(!isPricingPage ? funnelEls : pricingPageEls)
      .sortBy((x) => x.order)
      .value();
  };

  const renderContent = () => {
    const showRedirectMsg = window.bbkFeatureFlags?.includes(
      FeatureFlagsE.SHOW_FUNNEL_REDIRECT_MESSAGE
    );

    if (!localState) {
      return null;
    }

    if (showLoader) {
      return <LoadingOverlay />;
    }

    const renderEls = orderRenderEls();

    const hideEls = isLifecycle && !isHostedPage;

    return (
      <Container funnel={{ layout }} isPricingPage={isPricingPage}>
        {showOpaqueLoader && (
          <LoadingOverlay
            message={
              showRedirectMsg
                ? 'Please wait — we’re taking you to the next page.'
                : 'Loading...'
            }
            opaque
            opaqueFull={isLifecycle}
          />
        )}
        {/*
          One boundary per section rather than one for the page: a header whose
          saved data no longer fits its template must not take the survey with
          it. `layout` is the reset key because editing content in the designer
          replaces it, which is what lets a fixed section reappear.
        */}
        {!hideEls &&
          renderEls.map((x) => (
            <RenderErrorBoundary
              key={x.key}
              label={`funnel section ${x.key}`}
              resetKey={layout}
              isAdminSurface={isAdminFunnelSurface(funnelContext)}
            >
              {x.el}
            </RenderErrorBoundary>
          ))}
        <div className="container">{renderModal()}</div>
        <Global
          styles={css`
            ${isPreview ? '.exit-survey' : 'body'} {
              line-height: 1.15;
            }

            ${isPreview ? '' : 'body,'} .bbk-designer-page-experience {
              background: ${emotionTheme.background};
              ${emotionTheme[ThemeKeys.GLOBAL_CSS]}
            }

            ${isPreview ? '.exit-survey, .BrightBox ' : 'body '} {
              ${emotionTheme[ThemeKeys.GLOBAL_CSS]}
            }
            :root {
              --pricing-page-headline-color: ${emotionTheme.titleFontColor};
              --pricing-page-text-font-text-color: ${emotionTheme.textColor};
              --pricing-page-font-family: ${emotionTheme.fontFamily};
              --pricing-page-button-bg-color: ${
                emotionTheme.buttonBackgroundColor || emotionTheme.primaryColor
              };
              --pricing-page-button-text-color: ${emotionTheme.buttonTextColor};
              --pricing-page-card-background-color: ${emotionTheme.cardBackground};
              --pricing-page-background-color: ${emotionTheme.background};
              --pricingtable-border-radius: ${emotionTheme.buttonBorderRadius}px;
              --pricingtable-badge-color: ${
                emotionTheme.offerCardBadgeColors?.[0]
              };
              --pricingtable-style-accent-color: ${emotionTheme.primaryColor};
            }
            .pricing-page {
              font-family: var(--pricing-page-font-family);
            }
            ${
              !isLifecycle
                ? ''
                : `
              body {
                background: transparent !important;
              }
              .exit-survey {
                padding: 0;
              }
              .bbk-loss-components {
                display: none;
              }
            `
            }
          `}
        />
      </Container>
    );
  };

  if (disableThemeProvider) return renderContent();
  return (
    <EmotionThemeProvider theme={emotionTheme}>
      {renderContent()}
    </EmotionThemeProvider>
  );
};
